import type { BookingDetail } from '@/shared/api'
import { cn } from '@/shared/lib/cn'
import { EmptyState, Icon, Input, Select, Skeleton, Textarea, WidgetError } from '@/shared/ui'
import { parseRubles, perNightLabel, nightsLabel } from '../model/money'
import { useBookingCreate } from '../model/use-booking-create'
import s from './NewBookingForm.module.css'

type NewBookingFormProps = {
  /** Бронь создана. Наверх уезжает `BookingDetail` из `201` — открывать карточку не за чем идти. */
  onCreated: (booking: BookingDetail) => void
  onCancel: () => void
}

/**
 * Тело и подвал модалки «Новая бронь» (prototype/Prototype.dc.html:1420-1486).
 *
 * **Монтируется, только пока модалка открыта** — `Dialog` (Radix) не рендерит содержимое закрытого
 * диалога вовсе. Отсюда бесплатно берётся то, что иначе пришлось бы делать руками: закрыл модалку —
 * умерли и поля, и ключ идемпотентности, и наблюдатель мутации. Следующее открытие начинается с
 * чистого листа, а не с чужой полузаполненной брони.
 *
 * Ошибку создания эта форма не рисует, и это не пропуск. Упавшая мутация печатает тост с
 * `X-Request-Id` (`MutationCache`, `app/providers/query-client.ts`), форма остаётся на экране со
 * всем, что человек напечатал, а повтор — это просто второе нажатие «Создать бронь». Оно уходит с
 * ТЕМ ЖЕ ключом идемпотентности (`use-booking-create.ts`), поэтому отдельная кнопка «Повторить»
 * не нужна: она была бы вторым способом сделать то же самое, и один из двух рано или поздно
 * сминтил бы новый ключ.
 */
export function NewBookingForm({ onCreated, onCancel }: NewBookingFormProps) {
  const { form, set, objectId, reference, nights, canCreate, blockReason, creating, submit } =
    useBookingCreate(onCreated)

  const totalRubles = parseRubles(form.total)

  return (
    <>
      <div className={s.body}>
        <section>
          <div className={s.legend}>Объект</div>

          {/*
           * Данные → ошибка → скелетон. Порядок не переставлять: упавший ФОНОВЫЙ перезапрос
           * оставляет `data` целой и ОДНОВРЕМЕННО выставляет `error` (HANDOFF §5), и ветка
           * `error ? … : data ? …` спрятала бы живой справочник.
           *
           * Пустой список объектов — не ошибка, а факт: у компании нет ни одной квартиры.
           * `<select>` без единой опции молча показал бы пустоту, а кнопка «Создать бронь»
           * гасла бы без объяснения. Говорим прямо.
           */}
          {reference.data ? (
            reference.data.objects.length > 0 ? (
              <Select
                aria-label="Объект"
                options={reference.data.objects.map((object) => ({
                  value: object.id,
                  label: object.label,
                }))}
                value={objectId}
                onChange={(event) => set('objectId', event.target.value)}
              />
            ) : (
              /*
               * `EmptyState` кита, а не `.label`: класс подписи поля (11px, `--ink-3`) — тот же,
               * которым набраны «Заезд» и «Сумма брони, ₽», — одел бы сообщение «бронь создать
               * физически не на что» в стиль ярлычка над пустым инпутом.
               *
               * Тон `--st-pending`, а не дефолтный `--st-in`: зелёный кружок означал бы «всё
               * хорошо», а тут ровно наоборот — дальше хода нет, и кнопка внизу мертва (`title` её
               * это и говорит). Обе поверхности `EmptyState` уже в таблице `tools/contrast.mjs`:
               * кружок 14% на `--tint-on` — видом «декор» (иконка `aria-hidden`, тот же смысл
               * стоит рядом словами), заголовок `--ink` и подпись `--ink-3` на `--panel` — текстом.
               *
               * Размер не подгоняем: своим `className` можно задать только отступы, а перебивать
               * `padding` кита классом той же специфичности (0,1,0) — лотерея порядка чанков, ровно
               * та мина, которую в этой же задаче обезвредили у `Dialog` и карточки брони.
               */
              <EmptyState
                icon="alert"
                title="Объектов нет"
                description="Сначала заведите квартиру в Студии."
                tint="var(--st-pending)"
              />
            )
          ) : reference.error ? (
            <WidgetError error={reference.error} retry={() => void reference.refetch()} />
          ) : (
            <Skeleton width="100%" height={38} radius={9} />
          )}
        </section>

        <section>
          <div className={s.legendRow}>
            <span className={s.legend}>Даты проживания</span>
            {/* Бейдж — только при валидных датах (макет: `sc-if nbDatesValid`). */}
            {nights > 0 && <span className={s.nights}>{nightsLabel(nights)}</span>}
          </div>

          <div className={s.pair}>
            <div className={s.cell}>
              <div className={s.label}>Заезд</div>
              <Input
                type="date"
                aria-label="Заезд"
                value={form.checkIn}
                onChange={(event) => set('checkIn', event.target.value)}
              />
            </div>
            <div className={s.cell}>
              <div className={s.label}>Выезд</div>
              <Input
                type="date"
                aria-label="Выезд"
                value={form.checkOut}
                onChange={(event) => set('checkOut', event.target.value)}
              />
            </div>
          </div>
        </section>

        <section>
          <div className={s.legend}>Площадка-источник</div>
          <div className={s.chips}>
            {/*
             * Площадки — с сервера. Пока справочник летит или упал, чипов нет: список площадок
             * фронт не знает и выдумать не может, а ошибку про справочник человек уже прочитал
             * выше, в секции «Объект». Показывать её второй раз незачем — при сбое остальные
             * просто ЗАМОЛКАЮТ (правило «ошибку показывает один», HANDOFF §5).
             */}
            {reference.data
              ? reference.data.platforms.map((platform) => (
                  <button
                    key={platform.code}
                    type="button"
                    className={cn(s.platform, form.platformCode === platform.code && s.platformOn)}
                    aria-pressed={form.platformCode === platform.code}
                    onClick={() =>
                      set('platformCode', form.platformCode === platform.code ? null : platform.code)
                    }
                  >
                    {platform.name}
                  </button>
                ))
              : !reference.error && (
                  <>
                    <Skeleton width={92} height={34} radius={8} />
                    <Skeleton width={78} height={34} radius={8} />
                    <Skeleton width={104} height={34} radius={8} />
                  </>
                )}
          </div>
        </section>

        <div className={s.divider} />

        <section>
          <div className={s.legend}>Гость</div>

          <div className={s.pair}>
            <Input
              aria-label="Имя и фамилия"
              placeholder="Имя и фамилия"
              value={form.guestName}
              onChange={(event) => set('guestName', event.target.value)}
            />
            <Input
              className={s.phone}
              aria-label="Телефон"
              placeholder="+7 ___ ___-__-__"
              inputMode="tel"
              value={form.guestPhone}
              onChange={(event) => set('guestPhone', event.target.value)}
            />
          </div>

          <div className={s.guestRow}>
            <div className={s.inline}>
              <span className={s.inlineLabel}>Канал:</span>
              <div className={s.channels}>
                {reference.data
                  ? reference.data.channels.map((channel) => (
                      <button
                        key={channel.value}
                        type="button"
                        className={cn(
                          s.channel,
                          form.guestChannel === channel.value && s.channelOn,
                        )}
                        aria-pressed={form.guestChannel === channel.value}
                        onClick={() =>
                          set(
                            'guestChannel',
                            form.guestChannel === channel.value ? null : channel.value,
                          )
                        }
                      >
                        {channel.label}
                      </button>
                    ))
                  : !reference.error && (
                      <>
                        <Skeleton width={86} height={30} radius={7} />
                        <Skeleton width={74} height={30} radius={7} />
                      </>
                    )}
              </div>
            </div>

            <div className={cn(s.inline, s.inlineEnd)}>
              <span className={s.inlineLabel}>Гостей:</span>
              <Input
                className={s.guests}
                aria-label="Гостей"
                inputMode="numeric"
                value={form.guests}
                onChange={(event) => set('guests', event.target.value)}
              />
            </div>
          </div>
        </section>

        <div className={s.divider} />

        <section>
          <div className={s.legend}>Оплата</div>

          {/*
           * Суммы человек вводит в РУБЛЯХ, контракт принимает КОПЕЙКИ. Умножение живёт в одном
           * месте — `buildBody` (`model/money.ts`), и оно закрыто тестом: ошибка здесь тихая и
           * ровно в сто раз.
           */}
          <div className={s.triple}>
            <div className={s.cell}>
              <div className={s.label}>Сумма брони, ₽</div>
              <Input
                className={s.money}
                aria-label="Сумма брони, ₽"
                placeholder="0"
                inputMode="numeric"
                value={form.total}
                onChange={(event) => set('total', event.target.value)}
              />
            </div>
            <div className={s.cell}>
              <div className={s.label}>Предоплата, ₽</div>
              <Input
                className={s.money}
                aria-label="Предоплата, ₽"
                placeholder="0"
                inputMode="numeric"
                value={form.prepay}
                onChange={(event) => set('prepay', event.target.value)}
              />
            </div>
            <div className={s.cell}>
              <div className={s.label}>Залог, ₽</div>
              <Input
                className={s.money}
                aria-label="Залог, ₽"
                placeholder="0"
                inputMode="numeric"
                value={form.deposit}
                onChange={(event) => set('deposit', event.target.value)}
              />
            </div>
          </div>

          {/*
           * ⚠️ ЭТО ДЕНЬГИ КАССЫ, А НЕ СЛОВА ПЛОЩАДКИ, и подпись под полями говорит об этом прямо.
           * Оба поля когда-то собирали числа и выбрасывали их молча — у сущности брони предоплаты
           * и залога нет вовсе. Теперь у домена есть настоящий вход: они уезжают ПЛАТЕЖАМИ, в одной
           * транзакции с бронью, поэтому исход «бронь создалась, залог потерялся» невозможен.
           *
           * Сводки «Остаток к оплате» здесь по-прежнему нет: остаток не поле формы, а разница,
           * и считает её свод на карточке брони — там, где видны все платежи, а не только эти два.
           */}
          {/*
           * ⚠️ ФРАЗА ГОВОРИТ, ГДЕ ЧТО ЛЕЖИТ, А НЕ ЧТО ПРИДЁТ. Сказать «суммы, о которых сообщила
           * площадка, ПРИДУТ отдельно» значит обещать будущее событие на экране, где бронь
           * заводят РУКАМИ. Данные площадки приезжают снапшотом провайдера по внешнему номеру, а у
           * ручной брони он свой (`manual:<uuid>`) и площадке неизвестен, так что приходить нечему.
           * Доказать обратное («не придут никогда») мы тоже не можем — прямого запрета в домене нет,
           * — поэтому фраза не утверждает ни того, ни другого: она называет место.
           */}
          <div className={s.hintUnder}>
            Это деньги, которые вы приняли сами. То, о чём сообщает площадка, показывается отдельной
            секцией карточки и правке не подлежит.
          </div>

          <div className={s.summary}>
            <div className={s.inline}>
              <span className={s.summaryKey}>За ночь</span>
              <span className={s.summaryValue}>{perNightLabel(totalRubles, nights)} ₽</span>
            </div>
          </div>
        </section>

        <div className={s.divider} />

        <section>
          <div className={s.legend}>Примечание</div>
          {/*
           * Примечание к брони — то же поле, что видно и правится в карточке. Здесь оно затем,
           * что заводящий бронь менеджер чаще всего и знает, что про неё стоит записать: поздний
           * заезд, состав гостей, договорённость по оплате.
           *
           * ⚠️ Это НЕ ПДн гостя: примечание видно всем, у кого есть доступ к брони. Заметки о
           * госте живут в его профиле и приходят только с отдельным правом.
           */}
          <Textarea
            rows={3}
            aria-label="Примечание к брони"
            placeholder="Например: заезд после 23:00, просили детскую кроватку"
            value={form.internalNotes}
            onChange={(event) => set('internalNotes', event.target.value)}
          />
          <div className={s.hintUnder}>
            Видно всем, у кого есть доступ к брони. Личные данные гостя сюда писать не нужно.
          </div>
        </section>
      </div>

      <div className={s.footer}>
        <span className={s.hint}>Бронь появится в шахматке и ленте</span>

        <div className={s.actions}>
          <button type="button" className={s.cancel} onClick={onCancel} disabled={creating}>
            Отмена
          </button>

          {/*
           * В макете заблокированная кнопка — это `<span>` (Design Composer не умеет иначе). У нас
           * настоящая `<button disabled>`: скринридер объявит «Создать бронь, недоступно», а `<span>`
           * не объявил бы ничего. Вид тот же — заливка `--sunk`, `--ink-4`, `cursor: not-allowed`,
           * стрелки нет.
           *
           * Гаснет она и пока летит запрос. Это НЕ дорисованное состояние: вид у него тот же
           * заблокированный, что нарисован в макете, и живёт он доли секунды — в отличие от
           * `disabled` у кнопки композера, который стал бы ОСНОВНЫМ видом панели (HANDOFF §5,
           * «Выдуманное состояние…»). Двойной клик он тоже не пропустит, но защита от дубля стоит
           * не на нём, а на ключе идемпотентности: кнопка гаснет СЛЕДУЮЩИМ рендером, а два клика
           * бывают и в одном тике.
           */}
          {/*
           * Подсказка НЕ зашита здесь строкой, и это починка, а не стиль. Зашитая — уже соврала:
           * валидация получила третье условие (`objectId`), а `title` остался про «даты и имя», и
           * при упавшем справочнике кнопка требовала того, что человек уже ввёл. Причина приезжает
           * из `blockReason` — она выводится там же, где `canCreate`, из тех же условий, и разойтись
           * с ним больше не может (`use-booking-create.ts`).
           */}
          <button
            type="button"
            className={s.create}
            disabled={!canCreate || creating}
            title={blockReason ?? undefined}
            onClick={submit}
          >
            Создать бронь
            {canCreate && !creating && (
              <Icon name="arrow-right" size={14} strokeWidth={2.2} />
            )}
          </button>
        </div>
      </div>
    </>
  )
}
